import React from 'react';
import { Autocomplete, Checkbox } from '@mui/material';
import { UseFormReturn, Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { get, has } from 'lodash';

import TextFieldDefault from '../../Posts/Forms/TextFieldDefault';
import withOptional from '../../../Utils/Posts/Form/withOptional';

interface SelectAutocompleteProps extends Partial<React.ComponentProps<typeof Autocomplete>> {
  errors?: any;
  form: UseFormReturn<any>;
  label?: string;
  name: string;
  placeholder: string;
  required?: boolean;
  errorPath?: string;
  valueFormatter?: (value: any) => any;
  inputProps?: any;
}

function SelectAutocomplete({
  errors, // sometimes errors from other field to show. pass errorMessage instead?
  form,
  getOptionLabel = (option: any) => option?.label || '',
  label,
  errorPath,
  name,
  placeholder,
  required,
  isOptionEqualToValue = (option: any, value: any) => option === value,
  valueFormatter = (value: any) => value,
  multiple,
  inputProps,
  ...rest
}: SelectAutocompleteProps) {
  const intl = useIntl();
  const { control, setValue } = form;
  const fullLabel = required === undefined ? label : withOptional(label, required, true);

  const validate = (value: any) => {
    return !value && required ? intl.formatMessage({ id: 'POST_FORM.ERRORS.FILL_FIELDS' }) : true;
  };

  return (
    <Controller
      control={control}
      name={name}
      rules={{ validate }}
      render={({ field: { ref, value, ...restRender } }: any) => (
        <Autocomplete
          {...restRender}
          value={value || (multiple ? [] : null)}
          isOptionEqualToValue={isOptionEqualToValue}
          autoHighlight
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          getOptionLabel={getOptionLabel}
          onChange={(_e: any, newValue: any) => {
            // setting the full option object on the form
            // it means the option object need to be build to fit
            // the form data structure before being passed to the component
            setValue(name, valueFormatter(newValue));
          }}
          renderInput={(params) => (
            <TextFieldDefault
              {...params}
              ref={ref}
              label={fullLabel}
              placeholder={placeholder}
              inputProps={{
                ...inputProps,
                ...params.inputProps,
              }}
              helperText={get(errors, name)?.message || get(errors, `${errorPath}.message`)}
              error={has(errors, name) || has(errors, `${errorPath}`)}
            />
          )}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            return (
              <li key={key} {...optionProps}>
                {multiple && <Checkbox checked={selected} disableRipple />}
                {getOptionLabel(option)}
              </li>
            );
          }}
          fullWidth
          multiple={multiple}
          {...rest}
        />
      )}
    />
  );
}

export default SelectAutocomplete;
